import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useSettings } from '../context/SettingsContext';
import ThemeToggle from './ThemeToggle';
import {
  LogOut, ShoppingCart, Box, BarChart2, PackageOpen,
  Settings, Layers, Palette, Ruler, FolderPlus, Search,
  Menu, Users, ChevronLeft, ChevronRight, MapPin, RefreshCcw, Truck, History, ShoppingBag
} from 'lucide-react';

const Layout = ({ children }) => {
  const { roli, logout, isPrincipal } = useAuth();
  const { theme } = useTheme();
  const { getShopName, getLogoUrl, logoUrl, settings: globalSettings } = useSettings();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMini, setIsMini] = useState(false);

  const isActive = (path) => location.pathname.startsWith(path);

  const menuAdmin = [
    { to: '/', label: 'Dashboard', icon: <BarChart2 size={18} />, section: 'Menaxhimi' },
    { to: '/shitje', label: 'Shitje', icon: <ShoppingCart size={18} />, section: 'Operacionet' },
    { to: '/historiku-shitjeve', label: 'Historiku i Shitjeve', icon: <History size={18} />, section: 'Operacionet' },
    { to: '/porosite-online', label: 'Porositë Online', icon: <ShoppingBag size={18} />, section: 'Operacionet' },
    { to: '/kthimprodukti', label: 'KthimProdukti', icon: <RefreshCcw size={18} />, section: 'Operacionet' },
    { to: '/produkte', label: 'Produktet', icon: <PackageOpen size={18} />, section: 'Operacionet' },
    { to: '/raporte', label: 'Raporte', icon: <BarChart2 size={18} />, section: 'Operacionet' },
    { to: '/harxhime', label: 'Harxhime', icon: <FolderPlus size={18} />, section: 'Operacionet' },
    { to: '/kerkimi-stok', label: 'Kërko Stok', icon: <Search size={18} />, section: 'Operacionet' },
    { to: '/kategorite', label: 'Kategoritë', icon: <Layers size={18} />, section: 'Konfigurime' },
    { to: '/ngjyrat', label: 'Ngjyrat', icon: <Palette size={18} />, section: 'Konfigurime' },
    { to: '/madhesite', label: 'Madhësitë', icon: <Ruler size={18} />, section: 'Konfigurime' },
    { to: '/furnizuesit', label: 'Furnizuesit', icon: <Truck size={18} />, section: 'Konfigurime' },
    { to: '/stoku', label: 'Stoku', icon: <Box size={18} />, section: 'Konfigurime' },
    { to: '/perdoruesit', label: 'Përdoruesit', icon: <Users size={18} />, section: 'Konfigurime' },
    { to: '/lokacionet', label: 'Lokacionet', icon: <MapPin size={18} />, section: 'Konfigurime' },
    { to: '/lloj-harxhimi', label: 'Llojet e Harxhimeve', icon: <Settings size={18} />, section: 'Konfigurime' },
  ];

  const menuMenaxher = [
    { to: '/', label: 'Dashboard', icon: <BarChart2 size={18} />, section: 'Menaxhimi' },
    { to: '/shitje', label: 'Shitje', icon: <ShoppingCart size={18} />, section: 'Operacionet' },
    { to: '/kthimprodukti', label: 'Kthim Produkti', icon: <RefreshCcw size={18} />, section: 'Operacionet' },
    { to: '/kerkimi-stok', label: 'Kërko Stok', icon: <Search size={18} />, section: 'Operacionet' },
    { to: '/harxhime', label: 'Harxhime', icon: <FolderPlus size={18} />, section: 'Operacionet' },
    { to: '/raporte', label: 'Raporte', icon: <BarChart2 size={18} />, section: 'Operacionet' },
    { to: '/stoku', label: 'Stoku', icon: <Box size={18} />, section: 'Konfigurime' },
  ];

  const menuShites = [
    { to: '/', label: 'Dashboard', icon: <BarChart2 size={18} />, section: 'Menaxhimi' },
    { to: '/shitje', label: 'Shitje', icon: <ShoppingCart size={18} />, section: 'Menaxhimi' },
    { to: '/kthimprodukti', label: 'Kthim Produkti', icon: <RefreshCcw size={18} />, section: 'Menaxhimi' },
    { to: '/kerkimi-stok', label: 'Kërko Stok', icon: <Search size={18} />, section: 'Menaxhimi' },
  ];

  const menuOnline = [
    { to: '/', label: 'Dashboard', icon: <BarChart2 size={18} />, section: 'Menaxhimi' },
    { to: '/porosite-online', label: 'Porositë Online', icon: <ShoppingBag size={18} />, section: 'Operacionet' },
  ];

  let menu = menuShites;
  if (roli === 'admin') menu = menuAdmin;
  else if (roli === 'menaxher') menu = menuMenaxher;
  else if (roli === 'online') menu = menuOnline;
  
  // Fshi furnizuesit nga menu nëse janë të çaktivizuar
  // Kontrollo saktë: nëse furnizuesit_aktiv është eksplicitisht false, fshi nga menu
  if (globalSettings && globalSettings.furnizuesit_aktiv === false) {
    menu = menu.filter(item => item.to !== '/furnizuesit');
  }
  
  // Shto Settings menu për principal user
  if (isPrincipal && !menu.find(item => item.to === '/settings')) {
    menu = [...menu, { to: '/settings', label: 'Cilësimet e Sistemit', icon: <Settings size={18} />, section: 'Sistem' }];
  }

  const groupedMenu = menu.reduce((acc, item) => {
    const group = item.section || 'Të tjera';
    acc[group] = [...(acc[group] || []), item];
    return acc;
  }, {});

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-dark-900 transition-colors duration-300">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm z-40 md:hidden transition-opacity duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Premium */}
      <aside className={`fixed md:static z-50 transform top-0 left-0 h-screen 
        ${theme === 'dark' ? 'bg-gradient-sidebar-dark' : 'bg-gradient-sidebar'} 
        shadow-premium-lg dark:shadow-premium-lg-dark flex flex-col transition-all duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
        ${isMini ? 'w-20' : 'w-72'} md:translate-x-0 
        border-r border-gray-200/50 dark:border-dark-700`}>

        {/* Logo dhe mini toggle */}
        <div className={`flex items-center justify-between p-5 border-b 
          ${theme === 'dark' ? 'border-dark-700 bg-dark-800/50' : 'border-gray-200/50 bg-white/50'} 
          backdrop-blur-sm`}>
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={logoUrl || getLogoUrl()}
                alt={getShopName()}
                className={`transition-all duration-300 ${isMini ? 'w-12' : 'w-16'} h-auto drop-shadow-sm object-contain`}
                onError={(e) => {
                  console.error('Error loading logo:', e.target.src);
                  if (e.target.src !== '/logo.png' && !e.target.src.includes('/logo.png')) {
                    e.target.src = '/logo.png';
                  }
                }}
                onLoad={() => {
                  console.log('Logo loaded successfully:', logoUrl || getLogoUrl());
                }}
                key={logoUrl || 'default'} // Force re-render kur logo ndryshon
              />
            </div>
            {!isMini && (
              <div className="flex flex-col">
                <span className="text-sm font-bold text-gradient-logo">{getShopName()}</span>
              </div>
            )}
          </div>
          <button
            onClick={() => setIsMini(!isMini)}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-lg 
              text-gray-500 dark:text-dark-400 
              hover:text-primary-600 dark:hover:text-primary-400 
              hover:bg-primary-50 dark:hover:bg-primary-900/20 
              transition-all duration-200"
            title={isMini ? 'Zgjero' : 'Minimizo'}
          >
            {isMini ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Menu */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {Object.entries(groupedMenu).map(([section, items]) => (
            <div key={section} className="animate-fade-in">
              {!isMini && (
                <h4 className="text-gray-500 dark:text-dark-400 text-xs uppercase font-bold px-4 mb-3 tracking-wider">
                  {section}
                </h4>
              )}
              <div className="space-y-1.5">
                {items.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    title={isMini ? item.label : ''}
                    className={`sidebar-link ${isActive(item.to) ? 'sidebar-link-active' : ''}`}
                    onClick={() => {
                      setSidebarOpen(false);
                    }}
                  >
                    <span className={isActive(item.to) ? 'text-white' : ''}>{item.icon}</span>
                    {!isMini && (
                      <span className={`text-sm ${isActive(item.to) ? 'text-white font-semibold' : 'font-medium'}`}>
                        {item.label}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Theme Toggle dhe Dil */}
        <div className={`p-4 border-t 
          ${theme === 'dark' ? 'border-dark-700 bg-dark-800/30' : 'border-gray-200/50 bg-white/30'} 
          backdrop-blur-sm space-y-3`}>
          {/* Theme Toggle */}
          {!isMini && (
            <div className="flex items-center justify-between px-2 py-2">
              <span className="text-xs font-medium text-gray-600 dark:text-dark-400">Theme</span>
              <ThemeToggle />
            </div>
          )}
          {isMini && (
            <div className="flex justify-center">
              <ThemeToggle />
            </div>
          )}
          
          {/* Logout Button */}
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 
              bg-gradient-to-r from-red-500 to-red-600 
              text-white text-sm font-semibold py-3 rounded-xl 
              hover:from-red-600 hover:to-red-700 
              shadow-md hover:shadow-premium dark:hover:shadow-premium-lg-dark
              transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            title="Dil"
          >
            <LogOut size={18} />
            {!isMini && <span>Dil</span>}
          </button>
        </div>
      </aside>

      {/* Topbar për mobile */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className={`md:hidden flex items-center justify-between 
          ${theme === 'dark' ? 'bg-dark-800/80 border-dark-700' : 'bg-white/80 border-gray-200/50'} 
          backdrop-blur-lg shadow-premium dark:shadow-premium-dark p-4 border-b`}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors duration-200"
          >
            <Menu size={24} className="text-gray-700 dark:text-gray-300" />
          </button>
          <img
            src={logoUrl || getLogoUrl()}
            alt={getShopName()}
            className="w-12 h-auto object-contain"
            onError={(e) => {
              if (e.target.src !== '/logo.png') {
                e.target.src = '/logo.png';
              }
            }}
            key={logoUrl} // Force re-render kur logo ndryshon
          />
          <div className="w-10 flex items-center justify-end">
            <ThemeToggle />
          </div>
        </div>

        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-dark-900 min-h-screen transition-colors duration-300">
          <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
